import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from './useApi';

const useNotifications = () => {
  const { isAuthenticated } = useAuth();
  const { apiCall } = useApi();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);

  // Récupérer les notifications
  const fetchNotifications = useCallback(async (pageNum = 1, append = false) => {
    if (!isAuthenticated) return;

    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams({
        page: pageNum.toString(),
        limit: '20'
      });

      const response = await apiCall(`/admin/notifications?${params}`);
      
      if (response.success) {
        const newNotifications = response.data || [];
        
        if (append) {
          setNotifications(prev => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }
        
        setHasMore(newNotifications.length === 20);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des notifications:', err);
      setError('Erreur lors du chargement des notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, apiCall]);

  // Récupérer le nombre de notifications non lues
  const fetchUnreadCount = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await apiCall('/admin/notifications/unread-count');
      if (response.success) {
        setUnreadCount(response.count || 0);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération du count:', err);
    }
  }, [isAuthenticated, apiCall]);

  // Charger plus de notifications
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchNotifications(page + 1, true);
    }
  }, [loading, hasMore, page, fetchNotifications]);

  // Marquer une notification comme lue
  const markAsRead = useCallback(async (notificationId) => {
    try {
      await apiCall(`/admin/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      
      // Mettre à jour localement
      setNotifications(prev =>
        prev.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      
      // Mettre à jour le count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Erreur lors du marquage comme lu:', err);
      throw err;
    }
  }, [apiCall]);

  // Marquer toutes les notifications comme lues
  const markAllAsRead = useCallback(async () => {
    try {
      await apiCall('/admin/notifications/mark-all-read', {
        method: 'PUT'
      });
      
      // Mettre à jour localement
      setNotifications(prev =>
        prev.map(notif => ({ ...notif, is_read: true, read_at: new Date().toISOString() }))
      );
      
      setUnreadCount(0);
    } catch (err) {
      console.error('Erreur lors du marquage tout comme lu:', err);
      throw err;
    }
  }, [apiCall]);

  // Supprimer une notification
  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await apiCall(`/admin/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      
      // Retirer localement
      setNotifications(prev => {
        const notification = prev.find(n => n.id === notificationId);
        const remaining = prev.filter(n => n.id !== notificationId);
        
        // Réduire le count si la notification supprimée n'était pas lue
        if (notification && !notification.is_read) {
          setUnreadCount(prevCount => Math.max(0, prevCount - 1));
        }
        
        return remaining;
      });
    } catch (err) {
      console.error('Erreur lors de la suppression:', err);
      throw err;
    }
  }, [apiCall]);

  // Rafraîchir toutes les données
  const refresh = useCallback(() => {
    setPage(1);
    fetchNotifications(1, false);
    fetchUnreadCount();
  }, [fetchNotifications, fetchUnreadCount]);

  // Charger les données initiales
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(1, false);
      fetchUnreadCount();
    }
  }, [isAuthenticated, fetchNotifications, fetchUnreadCount]);

  // Polling pour les mises à jour (toutes les 30 secondes)
  useEffect(() => {
    if (!isAuthenticated) return;

    const interval = setInterval(() => {
      fetchUnreadCount();
    }, 30000); // 30 secondes

    return () => clearInterval(interval);
  }, [isAuthenticated, fetchUnreadCount]);

  return {
    // Data
    notifications,
    unreadCount,
    loading,
    error,
    hasMore,
    
    // Actions
    refresh,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    
    // Utils
    isAuthenticated
  };
};

export default useNotifications;